import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft,
  Shield,
  Gavel,
  AlertTriangle,
  FileText,
  Globe,
  Users,
  Clock,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useAuth0Context } from "@/contexts/Auth0Context";
import { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";

export const Terms = () => {
  const { isAuthenticated: legacyAuth, logout: legacyLogout } = useAuth();
  const {
    isAuthenticated: auth0Authenticated,
    loginWithRedirect,
    isLoading,
    user: auth0User,
    logout: auth0Logout,
  } = useAuth0Context();

  const handleSignIn = async () => {
    try {
      await loginWithRedirect();
    } catch (error) {
      console.error("Sign in error:", error);
    }
  };

  const handleSignUp = async () => {
    try {
      // For Auth0, we can pass screen_hint=signup to show signup form
      await loginWithRedirect();
    } catch (error) {
      console.error("Sign up error:", error);
    }
  };

  // Show loading state while Auth0 is initializing
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen text-white landing-dark">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-gray-900/80 backdrop-blur-lg border-b border-gray-700">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <a href="/" className="text-2xl font-bold gradient-text-purple">
                SnapLading
              </a>
            </div>
            <div className="flex items-center gap-4">
              {auth0Authenticated || legacyAuth ? (
                <>
                  <Button
                    size="sm"
                    className="button-purple button-modern"
                    asChild
                  >
                    <a href="/app/upload">Dashboard</a>
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-gray-300 hover:text-white"
                    onClick={() => {
                      if (auth0Authenticated) {
                        auth0Logout();
                      } else {
                        legacyLogout();
                      }
                    }}
                  >
                    Sign Out
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-gray-300 hover:text-white"
                    onClick={handleSignIn}
                  >
                    Sign In
                  </Button>
                  <Button
                    size="sm"
                    className="button-purple button-modern"
                    onClick={handleSignUp}
                  >
                    Sign Up
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 lg:px-8 relative bg-black">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center space-y-8">
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight">
              <span className="text-white">Terms & </span>
              <span className="gradient-text-purple">Conditions</span>
            </h1>
            <p className="text-xl sm:text-2xl text-gray-300 max-w-3xl mx-auto">
              Legal terms governing the use of SnapLading services and platform.
            </p>
          </div>
        </div>
      </section>

      {/* Terms Content */}
      <section className="py-20 px-4 lg:px-8 gradient-bg-purple-grey">
        <div className="container mx-auto max-w-4xl">
          <div className="space-y-12">
            {/* Introduction */}
            <Card className="bg-gray-900 border-gray-800">
              <CardHeader>
                <CardTitle className="text-2xl text-white flex items-center gap-3">
                  <FileText className="w-6 h-6 text-purple-400" />
                  Agreement to Terms
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-gray-300">
                <p>
                  These Terms and Conditions ("Terms") constitute a legally
                  binding agreement between you ("User", "Customer", or "you")
                  and Open Verify Inc. ("SnapLading", "Company", "we", "us", or
                  "our") governing your use of our AI-powered logistics platform
                  and related services.
                </p>
                <p>
                  <strong>Effective Date:</strong>{" "}
                  {new Date().toLocaleDateString()}
                </p>
                <p>
                  <strong>Jurisdiction:</strong> These Terms are governed by the
                  laws of the Province of Ontario, Canada, and the federal laws
                  of Canada applicable therein.
                </p>
                <div className="bg-red-900/20 border border-red-600/30 rounded-lg p-4">
                  <h4 className="font-semibold text-white mb-2">
                    IMPORTANT NOTICE
                  </h4>
                  <p>
                    By using our services, you acknowledge and agree that you
                    have read, understood, and accept these Terms in their
                    entirety. These Terms contain important limitations on your
                    rights and remedies, including mandatory arbitration and
                    liability limitations.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Agreement Through Use */}
            <Card className="bg-red-900/20 border-red-600/30">
              <CardHeader>
                <CardTitle className="text-2xl text-white flex items-center gap-3">
                  <AlertTriangle className="w-6 h-6 text-red-400" />
                  Agreement Through Use
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-gray-300">
                <div className="bg-red-900/40 border border-red-600/50 rounded-lg p-4">
                  <h4 className="font-semibold text-white mb-2">AGREEMENT</h4>
                  <p>
                    <strong>
                      ANY USE OF THE APPLICATION CONSTITUTES AGREEMENT TO THESE
                      TERMS.
                    </strong>{" "}
                    By accessing, using, or interacting with our services in any
                    manner, including but not limited to uploading documents,
                    processing data, generating EDI documents, or accessing our
                    platform, you automatically and irrevocably agree to be
                    bound by these Terms and Conditions in their entirety.
                  </p>
                </div>

                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-white mb-2">
                      Actions Constituting Agreement
                    </h4>
                    <ul className="space-y-2 ml-6">
                      <li className="flex items-start gap-2">
                        <span className="text-purple-400">•</span>
                        <span>
                          <strong>Accessing the Platform:</strong> Any access to
                          our website, API, or services
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-purple-400">•</span>
                        <span>
                          <strong>Uploading Documents:</strong> Submitting any
                          documents for processing
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-purple-400">•</span>
                        <span>
                          <strong>Using Services:</strong> Any interaction with
                          our AI processing capabilities
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-purple-400">•</span>
                        <span>
                          <strong>Downloading Results:</strong> Accessing or
                          downloading any generated data or documents
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-purple-400">•</span>
                        <span>
                          <strong>API Integration:</strong> Using our APIs or
                          integration services
                        </span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-yellow-900/20 border border-yellow-600/30 rounded-lg p-4">
                    <h4 className="font-semibold text-white mb-2">
                      NO CONSENT REQUIRED OTHER THAN CONSENT GIVEN THROUGH THESE
                      TERMS
                    </h4>
                    <p>
                      You acknowledge that there is no need for separate written
                      agreement or verbal agreement for these Terms to be
                      binding. Your continued use of our services after any
                      changes to these Terms constitutes acceptance of the
                      updated Terms. If you do not agree to these Terms, you
                      must immediately cease all use of our services.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* System Abuse and Termination */}
            <Card className="bg-red-900/20 border-red-600/30">
              <CardHeader>
                <CardTitle className="text-2xl text-white flex items-center gap-3">
                  <AlertTriangle className="w-6 h-6 text-red-400" />
                  System Abuse and Termination
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-gray-300">
                <div className="bg-red-900/40 border border-red-600/50 rounded-lg p-4">
                  <h4 className="font-semibold text-white mb-2">
                    ZERO TOLERANCE FOR SYSTEM ABUSE
                  </h4>
                  <p>
                    <strong>
                      ABUSE OF THE SYSTEM WILL RESULT IN IMMEDIATE TERMINATION
                      AND POTENTIAL LEGAL ACTION.
                    </strong>{" "}
                    We maintain a zero-tolerance policy for any form of system
                    abuse, misuse, or violation of these Terms.
                  </p>
                </div>

                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-white mb-2">
                      Prohibited Activities
                    </h4>
                    <ul className="space-y-2 ml-6">
                      <li className="flex items-start gap-2">
                        <span className="text-purple-400">•</span>
                        <span>
                          <strong>Excessive Usage:</strong> Unreasonable or
                          excessive use of system resources
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-purple-400">•</span>
                        <span>
                          <strong>Unauthorized Access:</strong> Attempting to
                          access systems or data without authorization
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-purple-400">•</span>
                        <span>
                          <strong>Data Manipulation:</strong> Attempting to
                          manipulate, corrupt, or interfere with data processing
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-purple-400">•</span>
                        <span>
                          <strong>Service Interference:</strong> Any actions
                          that interfere with service availability or
                          performance
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-purple-400">•</span>
                        <span>
                          <strong>Violation of Terms:</strong> Any breach of
                          these Terms and Conditions
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-purple-400">•</span>
                        <span>
                          <strong>Illegal Activities:</strong> Using our
                          services for any illegal or unauthorized purpose
                        </span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-yellow-900/20 border border-yellow-600/30 rounded-lg p-4">
                    <h4 className="font-semibold text-white mb-2">
                      IMMEDIATE TERMINATION
                    </h4>
                    <p>
                      <strong>IMMEDIATE TERMINATION:</strong> Any violation of
                      these Terms or system abuse will result in immediate
                      termination of your account and access to our services
                      without prior notice or warning.
                    </p>
                    <ul className="space-y-1 ml-6 mt-2">
                      <li className="flex items-start gap-2">
                        <span className="text-purple-400">•</span>
                        <span>Account suspension or termination</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-purple-400">•</span>
                        <span>Immediate cessation of all services</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-purple-400">•</span>
                        <span>Loss of access to all data and documents</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-purple-400">•</span>
                        <span>
                          Forfeiture of any prepaid services or credits
                        </span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-red-900/40 border border-red-600/50 rounded-lg p-4">
                    <h4 className="font-semibold text-white mb-2">
                      POTENTIAL LEGAL ACTION
                    </h4>
                    <p>
                      <strong>LEGAL CONSEQUENCES:</strong> System abuse may
                      result in legal action, including but not limited to:
                    </p>
                    <ul className="space-y-1 ml-6 mt-2">
                      <li className="flex items-start gap-2">
                        <span className="text-purple-400">•</span>
                        <span>
                          <strong>Civil Litigation:</strong> Legal proceedings
                          to recover damages and costs
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-purple-400">•</span>
                        <span>
                          <strong>Criminal Charges:</strong> Referral to law
                          enforcement for criminal violations
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-purple-400">•</span>
                        <span>
                          <strong>Injunctions:</strong> Court orders to prevent
                          further violations
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-purple-400">•</span>
                        <span>
                          <strong>Monetary Damages:</strong> Recovery of all
                          costs, damages, and attorney fees
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-purple-400">•</span>
                        <span>
                          <strong>Regulatory Reporting:</strong> Reporting
                          violations to relevant authorities
                        </span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-yellow-900/20 border border-yellow-600/30 rounded-lg p-4">
                    <h4 className="font-semibold text-white mb-2">
                      NO APPEAL PROCESS
                    </h4>
                    <p>
                      <strong>TERMINATION DECISIONS ARE FINAL.</strong>{" "}
                      Decisions to terminate accounts due to system abuse or
                      Terms violations are made at our sole discretion and are
                      not subject to appeal or reconsideration.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Service Description */}
            <Card className="bg-gray-900 border-gray-800">
              <CardHeader>
                <CardTitle className="text-2xl text-white flex items-center gap-3">
                  <Shield className="w-6 h-6 text-purple-400" />
                  Service Description
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-gray-300">
                <p>
                  SnapLading provides AI-powered document processing services
                  for bills of lading and logistics documents, including:
                </p>
                <ul className="space-y-2 ml-6">
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    <span>Document intelligence and OCR processing</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    <span>EDI X12 generation (204, 211 formats)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    <span>Data extraction and mapping services</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    <span>API and SFTP integration services</span>
                  </li>
                </ul>
                <p>
                  <strong>AS-IS BASIS:</strong> All services are provided on an
                  "AS-IS" and "AS-AVAILABLE" basis without any warranties,
                  representations, or guarantees of any kind.
                </p>
              </CardContent>
            </Card>

            {/* Data Validation and User Responsibility */}
            <Card className="bg-red-900/20 border-red-600/30">
              <CardHeader>
                <CardTitle className="text-2xl text-white flex items-center gap-3">
                  <AlertTriangle className="w-6 h-6 text-red-400" />
                  Data Validation and User Responsibility
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-gray-300">
                <div className="bg-red-900/40 border border-red-600/50 rounded-lg p-4">
                  <h4 className="font-semibold text-white mb-2">
                    CRITICAL USER OBLIGATION
                  </h4>
                  <p>
                    <strong>
                      YOU ARE REQUIRED TO IMPLEMENT AND MAINTAIN A DOWNSTREAM
                      VALIDATION PROCESS.
                    </strong>{" "}
                    The intent and requirement of this application is for you to
                    have a downstream process that validates and qualifies any
                    data generated by the application before using such data in
                    any business operations, regulatory filings, or commercial
                    transactions.
                  </p>
                </div>

                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-white mb-2">
                      Mandatory Validation Requirements
                    </h4>
                    <ul className="space-y-2 ml-6">
                      <li className="flex items-start gap-2">
                        <span className="text-purple-400">•</span>
                        <span>
                          <strong>Downstream Validation:</strong> You must
                          implement and maintain a comprehensive validation
                          process for all data generated by our services
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-purple-400">•</span>
                        <span>
                          <strong>Data Qualification:</strong> All extracted
                          data, EDI documents, and generated outputs must be
                          validated before use
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-purple-400">•</span>
                        <span>
                          <strong>Quality Assurance:</strong> You are
                          responsible for ensuring accuracy, completeness, and
                          compliance of all processed data
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-purple-400">•</span>
                        <span>
                          <strong>Regulatory Compliance:</strong> You must
                          verify compliance with all applicable regulations
                          before using generated data
                        </span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-yellow-900/20 border border-yellow-600/30 rounded-lg p-4">
                    <h4 className="font-semibold text-white mb-2">
                      EXAMPLES OF REQUIRED VALIDATION
                    </h4>
                    <p className="mb-2">
                      You must validate all aspects of generated data, including
                      but not limited to:
                    </p>
                    <ul className="space-y-1 ml-6">
                      <li className="flex items-start gap-2">
                        <span className="text-purple-400">•</span>
                        <span>
                          Dates, addresses, quantities, and measurements
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-purple-400">•</span>
                        <span>
                          Hazmat information, classifications, and safety data
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-purple-400">•</span>
                        <span>Shipper and consignee information</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-purple-400">•</span>
                        <span>EDI X12 document structure and content</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-purple-400">•</span>
                        <span>
                          Regulatory compliance and documentation requirements
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Complete Data Liability Indemnification */}
            <Card className="bg-red-900/20 border-red-600/30">
              <CardHeader>
                <CardTitle className="text-2xl text-white flex items-center gap-3">
                  <AlertTriangle className="w-6 h-6 text-red-400" />
                  Complete Data Liability Indemnification
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-gray-300">
                <div className="bg-red-900/40 border border-red-600/50 rounded-lg p-4">
                  <h4 className="font-semibold text-white mb-2">
                    COMPLETE INDEMNIFICATION FOR DATA ERRORS
                  </h4>
                  <p>
                    <strong>
                      OPEN VERIFY INC. IS COMPLETELY INDEMNIFIED FROM ALL
                      DATA-RELATED LIABILITIES.
                    </strong>{" "}
                    You hereby completely and irrevocably indemnify, defend, and
                    hold harmless Open Verify Inc., its owners, directors,
                    officers, employees, agents, contractors, partners,
                    subsidiaries, affiliates, licensors, and service providers
                    (collectively, "Indemnified Parties") from and against any
                    and all claims, demands, actions, suits, proceedings,
                    damages, losses, costs, expenses, fines, penalties, and
                    liabilities of any kind whatsoever arising from or related
                    to:
                  </p>
                </div>

                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-white mb-2">
                      Data Error Indemnification
                    </h4>
                    <ul className="space-y-2 ml-6">
                      <li className="flex items-start gap-2">
                        <span className="text-purple-400">•</span>
                        <span>
                          <strong>Data Accuracy:</strong> Any errors,
                          inaccuracies, or misinformation in extracted or
                          generated data
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-purple-400">•</span>
                        <span>
                          <strong>EDI Document Errors:</strong> Any faults,
                          errors, or inaccuracies in EDI X12 documents (204,
                          211, etc.)
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-purple-400">•</span>
                        <span>
                          <strong>Missing Information:</strong> Any missing,
                          incomplete, or omitted data or documentation
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-purple-400">•</span>
                        <span>
                          <strong>Regulatory Violations:</strong> Any regulatory
                          violations, fines, or penalties resulting from data
                          errors
                        </span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-yellow-900/20 border border-yellow-600/30 rounded-lg p-4">
                    <h4 className="font-semibold text-white mb-2">
                      SPECIFIC EXAMPLES OF INDEMNIFIED LIABILITIES
                    </h4>
                    <p className="mb-2">
                      You are solely responsible for all costs, damages, and
                      liabilities arising from:
                    </p>
                    <ul className="space-y-1 ml-6">
                      <li className="flex items-start gap-2">
                        <span className="text-purple-400">•</span>
                        <span>
                          <strong>Wrong dates, addresses, or quantities</strong>{" "}
                          in generated documents
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-purple-400">•</span>
                        <span>
                          <strong>
                            Missing or incorrect hazmat information
                          </strong>{" "}
                          and classifications
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-purple-400">•</span>
                        <span>
                          <strong>Faulty EDI 211 bills of lading</strong> with
                          incorrect data
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-purple-400">•</span>
                        <span>
                          <strong>Regulatory fines and penalties</strong> for
                          non-compliant documentation
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-purple-400">•</span>
                        <span>
                          <strong>Business losses</strong> resulting from data
                          errors or misinformation
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-purple-400">•</span>
                        <span>
                          <strong>Third-party claims</strong> arising from data
                          inaccuracies
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-purple-400">•</span>
                        <span>
                          <strong>Legal costs and attorney fees</strong> related
                          to data disputes
                        </span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-red-900/40 border border-red-600/50 rounded-lg p-4">
                    <h4 className="font-semibold text-white mb-2">
                      NO LIABILITY ACKNOWLEDGMENT
                    </h4>
                    <p>
                      <strong>
                        OPEN VERIFY INC. HAS NO LEGAL OR FINANCIAL
                        RESPONSIBILITY.
                      </strong>{" "}
                      Open Verify Inc., its owners, employees, contractors,
                      partners, and subsidiaries are not in any way legally or
                      financially responsible for any data errors,
                      misinformation, regulatory violations, fines, penalties,
                      or downstream costs or liabilities created from the use of
                      our services. All such liabilities, costs, fines, and
                      damages are solely the responsibility of the user.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Complete Indemnification */}
            <Card className="bg-gray-900 border-gray-800">
              <CardHeader>
                <CardTitle className="text-2xl text-white flex items-center gap-3">
                  <Shield className="w-6 h-6 text-purple-400" />
                  Complete Indemnification and Liability Release
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-gray-300">
                <div className="bg-red-900/20 border border-red-600/30 rounded-lg p-4">
                  <h4 className="font-semibold text-white mb-2">
                    COMPLETE INDEMNIFICATION
                  </h4>
                  <p>
                    You hereby completely and irrevocably indemnify, defend, and
                    hold harmless Open Verify Inc., its owners, directors,
                    officers, employees, agents, partners, subsidiaries,
                    affiliates, licensors, and service providers (collectively,
                    "Indemnified Parties") from and against any and all claims,
                    demands, actions, suits, proceedings, damages, losses,
                    costs, expenses, and liabilities of any kind whatsoever,
                    including but not limited to:
                  </p>
                </div>

                <ul className="space-y-2 ml-6">
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    <span>
                      <strong>Service Quality:</strong> Any claims related to
                      service quality, accuracy, reliability, or performance
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    <span>
                      <strong>Service Availability:</strong> Any claims related
                      to service downtime, interruptions, or unavailability
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    <span>
                      <strong>Data Processing:</strong> Any claims related to
                      data processing, storage, or transmission
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    <span>
                      <strong>Business Losses:</strong> Any direct, indirect,
                      incidental, special, consequential, or punitive damages
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    <span>
                      <strong>Third-Party Claims:</strong> Any claims arising
                      from third-party services or integrations
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    <span>
                      <strong>Regulatory Compliance:</strong> Any claims related
                      to regulatory or compliance issues
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    <span>
                      <strong>Intellectual Property:</strong> Any claims related
                      to intellectual property rights
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    <span>
                      <strong>Personal Injury:</strong> Any claims for personal
                      injury, death, or property damage
                    </span>
                  </li>
                </ul>

                <div className="bg-yellow-900/20 border border-yellow-600/30 rounded-lg p-4">
                  <h4 className="font-semibold text-white mb-2">
                    NO LIABILITY ACKNOWLEDGMENT
                  </h4>
                  <p>
                    You expressly acknowledge and agree that the Indemnified
                    Parties shall have no liability whatsoever for any damages,
                    losses, or injuries arising from or related to the use of
                    our services, regardless of the cause or nature of such
                    damages, losses, or injuries.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Disclaimers */}
            <Card className="bg-gray-900 border-gray-800">
              <CardHeader>
                <CardTitle className="text-2xl text-white flex items-center gap-3">
                  <AlertTriangle className="w-6 h-6 text-purple-400" />
                  Disclaimers and Warranties
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-gray-300">
                <div className="bg-red-900/20 border border-red-600/30 rounded-lg p-4">
                  <h4 className="font-semibold text-white mb-2">
                    DISCLAIMER OF ALL WARRANTIES
                  </h4>
                  <p>
                    TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, THE
                    INDEMNIFIED PARTIES EXPRESSLY DISCLAIM ALL WARRANTIES,
                    REPRESENTATIONS, AND GUARANTEES OF ANY KIND, WHETHER
                    EXPRESS, IMPLIED, STATUTORY, OR OTHERWISE, INCLUDING BUT NOT
                    LIMITED TO:
                  </p>
                </div>

                <ul className="space-y-2 ml-6">
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    <span>
                      <strong>Warranties of Merchantability:</strong> Any
                      warranties that services are merchantable or fit for a
                      particular purpose
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    <span>
                      <strong>Warranties of Accuracy:</strong> Any warranties
                      regarding the accuracy, completeness, or reliability of
                      results
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    <span>
                      <strong>Warranties of Availability:</strong> Any
                      warranties regarding service availability, uptime, or
                      performance
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    <span>
                      <strong>Warranties of Security:</strong> Any warranties
                      regarding data security or protection
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    <span>
                      <strong>Warranties of Non-Infringement:</strong> Any
                      warranties regarding intellectual property rights
                    </span>
                  </li>
                </ul>

                <p>
                  <strong>NO GUARANTEES:</strong> We make no guarantees
                  regarding service quality, accuracy, availability, or
                  performance. All services are provided without any assurances
                  or promises of any kind.
                </p>
              </CardContent>
            </Card>

            {/* Arbitration Clause */}
            <Card className="bg-gray-900 border-gray-800">
              <CardHeader>
                <CardTitle className="text-2xl text-white flex items-center gap-3">
                  <Gavel className="w-6 h-6 text-purple-400" />
                  Mandatory Arbitration
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-gray-300">
                <div className="bg-red-900/20 border border-red-600/30 rounded-lg p-4">
                  <h4 className="font-semibold text-white mb-2">
                    MANDATORY ARBITRATION - NO COURT PROCEEDINGS
                  </h4>
                  <p>
                    ANY AND ALL DISPUTES, CLAIMS, OR CONTROVERSIES ARISING FROM
                    OR RELATING TO THESE TERMS OR THE USE OF OUR SERVICES SHALL
                    BE RESOLVED EXCLUSIVELY THROUGH BINDING ARBITRATION IN
                    ONTARIO, CANADA. NO LEGAL PROCEEDINGS MAY BE BROUGHT IN ANY
                    COURT OF LAW.
                  </p>
                </div>

                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-white mb-2">
                      Arbitration Requirements
                    </h4>
                    <ul className="space-y-2 ml-6">
                      <li className="flex items-start gap-2">
                        <span className="text-purple-400">•</span>
                        <span>
                          <strong>Exclusive Forum:</strong> Arbitration is the
                          sole and exclusive forum for resolving any disputes
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-purple-400">•</span>
                        <span>
                          <strong>Jurisdiction:</strong> All arbitration must
                          take place in Ontario, Canada
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-purple-400">•</span>
                        <span>
                          <strong>Governing Law:</strong> Ontario, Canada law
                          governs all arbitration proceedings
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-purple-400">•</span>
                        <span>
                          <strong>No Class Actions:</strong> No class action or
                          representative proceedings are permitted
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-purple-400">•</span>
                        <span>
                          <strong>Binding Decision:</strong> Arbitration
                          decisions are final and binding on all parties
                        </span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-yellow-900/20 border border-yellow-600/30 rounded-lg p-4">
                    <h4 className="font-semibold text-white mb-2">
                      MONETARY LIMITATION
                    </h4>
                    <p>
                      <strong>MAXIMUM RECOVERY:</strong> The maximum amount that
                      may be awarded in any arbitration proceeding is $1,000 CAD
                      (one thousand Canadian dollars), regardless of the nature
                      or extent of any damages, losses, or injuries claimed.
                    </p>
                  </div>

                  <div>
                    <h4 className="font-semibold text-white mb-2">
                      Arbitration Process
                    </h4>
                    <ul className="space-y-2 ml-6">
                      <li className="flex items-start gap-2">
                        <span className="text-purple-400">•</span>
                        <span>
                          Arbitration shall be conducted by a single arbitrator
                          appointed by mutual agreement
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-purple-400">•</span>
                        <span>Arbitration shall be conducted in English</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-purple-400">•</span>
                        <span>
                          Each party shall bear their own costs and expenses
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-purple-400">•</span>
                        <span>
                          Arbitration shall be conducted on an expedited basis
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Jurisdiction and Governing Law */}
            <Card className="bg-gray-900 border-gray-800">
              <CardHeader>
                <CardTitle className="text-2xl text-white flex items-center gap-3">
                  <Globe className="w-6 h-6 text-purple-400" />
                  Jurisdiction and Governing Law
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-gray-300">
                <div className="bg-purple-900/20 border border-purple-600/30 rounded-lg p-4">
                  <h4 className="font-semibold text-white mb-2">
                    ONTARIO, CANADA JURISDICTION
                  </h4>
                  <p>
                    These Terms are governed by and construed in accordance with
                    the laws of the Province of Ontario, Canada, and the federal
                    laws of Canada applicable therein. This agreement is
                    designed to be legally binding globally with Ontario, Canada
                    as the exclusive jurisdiction.
                  </p>
                </div>

                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-white mb-2">
                      Jurisdictional Provisions
                    </h4>
                    <ul className="space-y-2 ml-6">
                      <li className="flex items-start gap-2">
                        <span className="text-purple-400">•</span>
                        <span>
                          <strong>Exclusive Jurisdiction:</strong> Ontario,
                          Canada has exclusive jurisdiction over all matters
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-purple-400">•</span>
                        <span>
                          <strong>Governing Law:</strong> Ontario law governs
                          interpretation and enforcement
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-purple-400">•</span>
                        <span>
                          <strong>Venue:</strong> All proceedings must take
                          place in Ontario, Canada
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-purple-400">•</span>
                        <span>
                          <strong>Enforcement:</strong> Terms are enforceable in
                          Ontario courts if necessary
                        </span>
                      </li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-semibold text-white mb-2">
                      International Application
                    </h4>
                    <p>
                      These Terms are designed to be legally binding and
                      enforceable globally, with Ontario, Canada as the
                      governing jurisdiction. Users from any country or
                      jurisdiction agree to submit to the exclusive jurisdiction
                      of Ontario, Canada for all purposes.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Service Limitations */}
            <Card className="bg-gray-900 border-gray-800">
              <CardHeader>
                <CardTitle className="text-2xl text-white flex items-center gap-3">
                  <AlertTriangle className="w-6 h-6 text-purple-400" />
                  Service Limitations and Exclusions
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-gray-300">
                <p>You acknowledge and agree to the following limitations:</p>
                <ul className="space-y-2 ml-6">
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    <span>
                      <strong>No Guaranteed Results:</strong> We do not
                      guarantee any specific results or outcomes
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    <span>
                      <strong>Service Interruptions:</strong> Services may be
                      interrupted, suspended, or terminated at any time
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    <span>
                      <strong>Data Accuracy:</strong> We do not guarantee the
                      accuracy or completeness of processed data
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    <span>
                      <strong>Third-Party Dependencies:</strong> Services may
                      depend on third-party systems and services
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    <span>
                      <strong>Technical Limitations:</strong> Services are
                      subject to technical limitations and constraints
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    <span>
                      <strong>Regulatory Changes:</strong> Services may be
                      affected by regulatory or legal changes
                    </span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* User Obligations */}
            <Card className="bg-gray-900 border-gray-800">
              <CardHeader>
                <CardTitle className="text-2xl text-white flex items-center gap-3">
                  <Users className="w-6 h-6 text-purple-400" />
                  User Obligations and Responsibilities
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-gray-300">
                <p>By using our services, you agree to:</p>
                <ul className="space-y-2 ml-6">
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    <span>
                      <strong>Compliance:</strong> Comply with all applicable
                      laws and regulations
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    <span>
                      <strong>Data Responsibility:</strong> Ensure you have
                      rights to process any data submitted
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    <span>
                      <strong>Security:</strong> Maintain appropriate security
                      measures for your account
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    <span>
                      <strong>Payment:</strong> Pay all fees and charges in a
                      timely manner
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    <span>
                      <strong>No Misuse:</strong> Not use services for any
                      illegal or unauthorized purpose
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    <span>
                      <strong>Indemnification:</strong> Indemnify us for any
                      claims arising from your use of services
                    </span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Termination */}
            <Card className="bg-gray-900 border-gray-800">
              <CardHeader>
                <CardTitle className="text-2xl text-white flex items-center gap-3">
                  <Clock className="w-6 h-6 text-purple-400" />
                  Termination and Survival
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-gray-300">
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-white mb-2">
                      Termination
                    </h4>
                    <ul className="space-y-2 ml-6">
                      <li className="flex items-start gap-2">
                        <span className="text-purple-400">•</span>
                        <span>
                          We may terminate immediately for breach of these Terms
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-purple-400">•</span>
                        <span>
                          Termination does not affect accrued rights or
                          obligations
                        </span>
                      </li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-semibold text-white mb-2">
                      Surviving Provisions
                    </h4>
                    <p>
                      The following provisions survive termination:
                      indemnification, disclaimers, arbitration clause,
                      jurisdiction, and any other provisions that by their
                      nature should survive.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Contact Information */}
            <Card className="bg-gray-900 border-gray-800">
              <CardHeader>
                <CardTitle className="text-2xl text-white flex items-center gap-3">
                  <FileText className="w-6 h-6 text-purple-400" />
                  Contact Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-gray-300">
                <p>For questions about these Terms, contact us:</p>
                <div className="space-y-2">
                  <p>
                    <strong>Email:</strong>{" "}
                    <a
                      href="mailto:legal@snaplading.com"
                      className="text-purple-400 hover:text-purple-300"
                    >
                      legal@snaplading.com
                    </a>
                  </p>
                  <p>
                    <strong>Address:</strong> Open Verify Inc., Ontario, Canada
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Final Acknowledgment */}
            <Card className="bg-red-900/20 border-red-600/30">
              <CardHeader>
                <CardTitle className="text-2xl text-white flex items-center gap-3">
                  <AlertTriangle className="w-6 h-6 text-red-400" />
                  Final Acknowledgment
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-gray-300">
                <div className="bg-red-900/40 border border-red-600/50 rounded-lg p-4">
                  <h4 className="font-semibold text-white mb-2">
                    LEGAL ACKNOWLEDGMENT
                  </h4>
                  <p>
                    By using our services, you acknowledge that you have read,
                    understood, and agree to be bound by these Terms in their
                    entirety.
                  </p>
                  <p className="mt-4">
                    <strong>
                      IF YOU DO NOT AGREE TO THESE TERMS, DO NOT USE OUR
                      SERVICES.
                    </strong>
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 lg:px-8 gradient-bg-purple-grey">
        <div className="container mx-auto max-w-7xl">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="col-span-2 md:col-span-1">
              <h3 className="text-2xl font-bold mb-4 gradient-text-purple">
                SnapLading
              </h3>
              <p className="text-sm text-white">
                AI agent bill of lading processing for modern logistics.
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-3 text-white">Product</h4>
              <ul className="space-y-2 text-sm text-white">
                <li>
                  <a
                    href="/docs"
                    className="text-white hover:text-purple-400 transition-colors"
                  >
                    Documentation
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-3 text-white">Company</h4>
              <ul className="space-y-2 text-sm text-white">
                <li>
                  <a
                    href="/careers"
                    className="text-white hover:text-purple-400 transition-colors"
                  >
                    Careers
                  </a>
                </li>
                <li>
                  <a
                    href="/contact"
                    className="text-white hover:text-purple-400 transition-colors"
                  >
                    Contact
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-3 text-white">Legal</h4>
              <ul className="space-y-2 text-sm text-black">
                <li>
                  <a
                    href="/privacy"
                    className="text-black hover:text-purple-400 transition-colors"
                  >
                    Privacy
                  </a>
                </li>
                <li>
                  <a
                    href="/terms"
                    className="text-black hover:text-purple-400 transition-colors"
                  >
                    Terms
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-8 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-black">
              © {new Date().getFullYear()} Open Verify Inc. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};
